import type { PressableProps } from "../../primitives";
import { ctaLineHeight, ctaLineHeightDesktop } from "../../tokens";
import { buttonIconColor, buttonIconStateClass, buttonSpinnerColor } from "../button/lib";
import type { SpinnerProps } from "../spinner";
import type { IconButtonSize, IconButtonVariant } from "./types";

/**
 * Icon colour per look: Button's, plus `on-brand`'s white (Figma's
 * `brand/on-static`, the App Bar's icon buttons) and, disabled, the brand
 * bar's attenuated text. Figma draws no disabled on-brand look.
 */
export const iconButtonIconColor: Record<IconButtonVariant, { default: string; disabled: string }> =
  {
    ...buttonIconColor,
    "on-brand": {
      default: "text-luce-brand-onStatic",
      disabled: "text-luce-brand-onStaticAttenuated",
    },
  };

/** The loading spinner's colour: Button's, and white on a brand bar. */
export const iconButtonSpinnerColorFor = (
  variant: IconButtonVariant,
  disabled: boolean,
): SpinnerProps["color"] =>
  disabled ? "disabled" : variant === "on-brand" ? "primary" : buttonSpinnerColor[variant];

/**
 * IconButton icon px per size, mobile / desktop: sm 12/16, md 16/24, lg 24/28.
 * Figma sizes it to the `_cta/line-height` variables, not one icon step up
 * (md is 16 / 24).
 */
export const iconButtonIconSize: Record<IconButtonSize, { mobile: number; desktop: number }> = {
  sm: { mobile: ctaLineHeight.sm, desktop: ctaLineHeightDesktop.sm },
  md: { mobile: ctaLineHeight.md, desktop: ctaLineHeightDesktop.md },
  lg: { mobile: ctaLineHeight.lg, desktop: ctaLineHeightDesktop.lg },
};

/**
 * Icon hover/active classes: Button's, except destructive-secondary, whose
 * icon darkens to #e51d1d on hover (Figma `262:14070`) and lightens to
 * #ff7878 while pressed (the designer, 2026-10-29). ActionButton shares it.
 */
export const iconButtonIconStateClass: Partial<Record<IconButtonVariant, string>> = {
  ...buttonIconStateClass,
  "destructive-secondary":
    "group-hover:text-luce-button-destructive-hover group-active:text-luce-button-destructive-pressed",
};

type IconButtonAccessibilityContext = {
  icon?: string;
  loading: boolean;
  disabled: boolean;
} & Pick<PressableProps, "accessibilityLabel" | "accessibilityState">;

export function getIconButtonAccessibilityLabel(context: IconButtonAccessibilityContext): string {
  // The type requires a label; the fallback covers untyped or cast callers
  return context.accessibilityLabel ?? context.icon ?? "Icon button";
}

export function getIconButtonAccessibilityState(
  context: IconButtonAccessibilityContext,
): PressableProps["accessibilityState"] {
  return {
    ...context.accessibilityState,
    disabled: context.disabled,
    busy: context.loading,
  };
}
